import type React from "react";
import { useCallback, useEffect, useState } from "react";

import styled, { css } from "styled-components";

import type { IconName } from "@similarweb/icons";
import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, rgba, fonts, setFont } from "@similarweb/styles";
import { MobileAndDesktopScreenshots } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { COMPANY_ANALYSIS_OVERVIEW, WEBSITE_ANALYSIS_PERFORMANCE } from "app/pages/sales-intelligence/constants/routes";
import { useCompanyDomainInfoQuery } from "app/pages/sales-intelligence/modules/company-analysis/queries/useCompanyDomainInfoQuery";
import WebsitePerformanceAppItem from "app/components/React/AppItemWithTooltip/WebsitePerformanceAppItem";
import { GA4VerifiedContainer } from "app/components/React/GAVerifiedIcon/GA4VerifiedContainer";
import { SpinnerLoading } from "app/components/spinner-loading/SpinnerLoading";
import { EPrivacyStatus } from "app/components/website-header/WebsiteHeaderReact";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { getIsNewEngagementIosMetrics } from "app/pages/app-intelligence/helpers";
import companyAnalysisTrackingService from "app/pages/sales-intelligence/modules/company-analysis/services/tracking/companyAnalysisTrackingService";
import { AssetsService } from "app/services/AssetsService";
import { allTrackers } from "app/services/track/track";
import { RetailInsightsCta } from "./RetailInsightsCta";
import { proNavigator } from "single-spa/react-app/ProNavigator";

// ----- Structure -----
const HeaderWrapper = styled.div<{ isLoading?: boolean }>`
    display: flex;
    flex-direction: row;
    height: auto;
    min-height: 272px;
    width: 100%;
    padding: 24px;
    margin-bottom: 50px;
    border-radius: 4px;
    box-shadow: 0px 3px 6px ${rgba(colorsPalettes.carbon[500], 0.08)};
    box-sizing: border-box;
    ${({ isLoading }) =>
        isLoading
            ? css`
                  background: #ffffff;
              `
            : css`
                  background:
                      linear-gradient(90deg, #ffffff 8.56%, rgba(255, 255, 255, 0) 54.21%),
                      radial-gradient(
                          38.36% 126.97% at 0.79% 79.32%,
                          rgba(0, 115, 255, 0.15) 0%,
                          rgba(255, 255, 255, 0) 100%
                      ),
                      radial-gradient(
                          37.51% 157.39% at 83.12% 10.83%,
                          rgba(0, 115, 255, 0.15) 0%,
                          rgba(255, 255, 255, 0) 100%
                      ),
                      radial-gradient(
                          23.44% 59.26% at 0.42% 0%,
                          rgba(255, 132, 0, 0.15) 0%,
                          rgba(255, 255, 255, 0) 100%
                      ),
                      radial-gradient(
                          24.48% 60.57% at 67.76% 90.07%,
                          rgba(255, 132, 0, 0.15) 0%,
                          rgba(255, 255, 255, 0) 100%
                      ),
                      radial-gradient(168.43% 350.01% at 57.43% 23.44%, #f6f8fb 1.42%, rgba(243, 247, 254, 0) 100%),
                      linear-gradient(0deg, #ffffff, #ffffff), #f6f8fb;
              `};

    ${SpinnerLoading} {
        top: 50%;
        transform: translateY(-50%);
    }
`;

const LeftSectionWrapper = styled.div`
    display: flex;
    height: 100%;
    max-width: 50%;
    min-width: 35%;
    flex-direction: column;
`;

const DescriptionText = styled.div`
    ${setFont({
        $family: fonts.$robotoFontFamily,
        $size: 14,
        $weight: 400,
        $color: colorsPalettes.midnight[600],
    })};
    width: 100%;
    margin-bottom: 24px;
    min-height: 64px;

    /* Truncate text after three lines */
    max-height: 3em;
    overflow: hidden;
`;

const MiddleSection = styled.div`
    display: flex;
    flex: 1 1 auto;
    height: 100%;
`;

const RightSectionWrapper = styled.div`
    display: flex;
    align-self: flex-end;
    height: 100%;
`;

// ----- Main Title -----
const LeftTitleWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    align-self: flex-start;
    margin-bottom: 24px;
`;

const IconWrapper = styled.div`
    width: 32px;
    height: 32px;
    margin-right: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid ${colorsPalettes.carbon[100]};
    box-sizing: border-box;
    border-radius: 8px;
    box-shadow: 0px 2px 4px ${rgba(colorsPalettes.carbon[500], 0.2)};
`;

const DomainTitle = styled.span`
    ${setFont({
        $family: fonts.$dmSansFontFamily,
        $size: 32,
        $weight: 500,
        $color: colorsPalettes.midnight[600],
    })};
    margin-right: 4px;
`;

const OutLink = styled.a`
    opacity: 0;
    margin-right: 8px;

    &:hover {
        transition: opacity 0.5s east-out;
        opacity: 1;
    }

    & > path {
        fill: ${colorsPalettes.carbon[500]};
    }
`;

const TitleAndOutlink = styled.div`
    display: flex;
    flex-direction: row;

    &:hover > ${OutLink} {
        transition: opacity 0.5s east-out;
        opacity: 1;
    }
`;

// ----- Related Apps -----
interface IAppsGroupProps {
    iconName: IconName;
    title: string;
    store: string;
    storeKey: string;
    relatedApps: IStoreInfo;
    className?: string;
    onClickHandler?: (title: string) => void;
    showMore: () => void;
    showBack: () => void;
    showMoreExpanded: boolean;
    noOfDisplayedApps: number;
}

const AppGroupTitle = styled.span`
    ${setFont({
        $family: fonts.$robotoFontFamily,
        $size: 13,
        $weight: 400,
        $color: colorsPalettes.midnight[600],
    })};
    margin-left: 4px;
`;

const AppGroupIcon = styled(SWReactIcons)`
    & > svg {
        & > path {
            fill: ${colorsPalettes.carbon[500]};
        }
    }
`;

const AppsGroup: React.FC<IAppsGroupProps> = ({
    iconName,
    title,
    store,
    relatedApps,
    className,
    onClickHandler,
    showMore,
    showBack,
    showMoreExpanded,
    noOfDisplayedApps = 3,
}) => {
    const translate = useTranslation();
    const hasMonthlyDownloads = getIsNewEngagementIosMetrics();
    return (
        <div className={className}>
            <RelatedAppsTitleWrapper>
                <AppGroupIcon
                    iconName={iconName}
                    size="xs"
                />
                <AppGroupTitle>{title}</AppGroupTitle>
            </RelatedAppsTitleWrapper>
            <AppIconsWrapper>
                <WebsitePerformanceAppItem
                    store={store}
                    apps={relatedApps.apps}
                    limitApps={noOfDisplayedApps}
                    onClick={onClickHandler}
                    aClassName="related-app"
                    imgClassName="related-apps-app-icon"
                    hideMonthlyDownloads={hasMonthlyDownloads}
                />
            </AppIconsWrapper>
            {!showMoreExpanded && relatedApps.apps.length > noOfDisplayedApps ? (
                <span
                    className="related-apps-show-more u-underline"
                    onClick={showMore}
                >
                    {translate("wa.ao.header.apps.more")}
                </span>
            ) : null}
            {showMoreExpanded ? (
                <span
                    className="related-apps-show-more u-underline"
                    onClick={showBack}
                >
                    {translate("wa.ao.header.apps.back")}
                </span>
            ) : null}
        </div>
    );
};

const RelatedAppsGroup = styled(AppsGroup)`
    display: flex;
    flex-direction: column;
    width: 50%;
`;

const AppIconsWrapper = styled.div`
    display: flex;
    flex-direction: row;
`;

const RelatedAppsTitleWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 4px;
`;

const RelatedAppsWrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;

    & > ${RelatedAppsGroup} {
        margin-right: 16px;
    }
`;

const getMobileScreenshotUrlFromDesktopImage = (desktopUrl: string): string => desktopUrl.replace("&t=1", "&t=4");

interface IAppInfo {
    appId: string;
    appStore: string;
}

interface IStoreInfo {
    apps: IAppInfo[];
    showMore: boolean;
    showBack: boolean;
}

interface IRelatedAppsProp {
    ios?: IStoreInfo;
    google?: IStoreInfo;
}

interface IWebsitePerformanceHeaderProps {
    domain: string;
    description?: string;
    favIconUrl: string;
    websiteHeaderImage?: string;
    privacyStatus: string;
    hasGAToken: boolean;
    relatedApps: IRelatedAppsProp;
    relatedAppsShowMore: (store: string) => void;
    relatedAppsShowBack: (store: string) => void;
    retailIntelligenceUpsellCtaHref?: string;
    loading?: boolean;
}

export const WebsitePerformanceHeader: React.FC<IWebsitePerformanceHeaderProps> = ({
    domain,
    description,
    favIconUrl,
    websiteHeaderImage = AssetsService.assetUrl("/images/no-image.png"),
    privacyStatus,
    hasGAToken,
    relatedApps,
    retailIntelligenceUpsellCtaHref,
    loading,
}) => {
    const translate = useTranslation();
    const [appstoreMoreOpen, setAppstoreMoreOpen] = useState(false);
    const [googleMoreOpen, setGoogleMoreOpen] = useState(false);

    useEffect(() => {
        if (!retailIntelligenceUpsellCtaHref) {
            return;
        }
        allTrackers.dispatchTrackEvent("button", "impression", "connect_retail_iq");
    }, [retailIntelligenceUpsellCtaHref]);

    const handleRetailInsightsClick = useCallback(() => {
        allTrackers.dispatchTrackEvent("button", "click", "connect_retail_iq");
    }, []);

    const isSIWebsiteAnalysisPage = proNavigator.isCurrentStateName(WEBSITE_ANALYSIS_PERFORMANCE);
    const { data: companyInfo } = useCompanyDomainInfoQuery(domain, isSIWebsiteAnalysisPage);
    const handleAppClick = (title: string) => {
        companyAnalysisTrackingService.website.trackCompanyAppClick(title, domain);
    };

    if (loading) {
        return (
            <HeaderWrapper isLoading={loading}>
                <SpinnerLoading />
            </HeaderWrapper>
        );
    }
    return (
        <HeaderWrapper isLoading={loading}>
            <LeftSectionWrapper>
                <LeftTitleWrapper>
                    <IconWrapper>
                        <img src={favIconUrl} />
                    </IconWrapper>
                    <TitleAndOutlink>
                        <DomainTitle>{domain}</DomainTitle>
                        <OutLink
                            href={`http://${domain}`}
                            target="_blank"
                        >
                            <SWReactIcons
                                iconName="link-out"
                                size="xs"
                            />
                        </OutLink>
                    </TitleAndOutlink>
                    {isSIWebsiteAnalysisPage ? null : (
                        <GA4VerifiedContainer
                            hasGAToken={hasGAToken}
                            isPrivate={privacyStatus === EPrivacyStatus.Private ? hasGAToken : null}
                        />
                    )}
                </LeftTitleWrapper>
                {isSIWebsiteAnalysisPage && companyInfo && (companyInfo.name || companyInfo.mainDomain) ? (
                    <Text
                        fontVariant="chip1"
                        margin="-12px 0 16px"
                    >
                        {translate("wa.ao.header.by")}
                        <a
                            href={proNavigator.href(COMPANY_ANALYSIS_OVERVIEW, { key: companyInfo?.mainDomain })}
                        >{` ${companyInfo.name || companyInfo.mainDomain}`}</a>
                    </Text>
                ) : null}
                <DescriptionText>{description}</DescriptionText>
                <Flex
                    flexDirection="column"
                    flex="0 0 auto"
                    margin="auto 0 0 0"
                    width="100%"
                >
                    <RelatedAppsWrapper>
                        {relatedApps?.ios?.apps?.length > 0 ? (
                            <RelatedAppsGroup
                                iconName="i-tunes"
                                store={relatedApps.ios.apps[0].appStore.toString()}
                                storeKey="ios"
                                title={translate("wa.ao.header.iosapps")}
                                relatedApps={relatedApps.ios}
                                showMoreExpanded={appstoreMoreOpen}
                                noOfDisplayedApps={appstoreMoreOpen ? relatedApps.ios.apps.length : 3}
                                showMore={setAppstoreMoreOpen.bind(null, true)}
                                showBack={setAppstoreMoreOpen.bind(null, false)}
                                onClickHandler={isSIWebsiteAnalysisPage ? handleAppClick : undefined}
                            />
                        ) : null}
                        {relatedApps?.google?.apps?.length > 0 ? (
                            <RelatedAppsGroup
                                iconName="google-play"
                                store={relatedApps.google.apps[0].appStore.toString()}
                                storeKey="google"
                                title={translate("wa.ao.header.googleapps")}
                                relatedApps={relatedApps.google}
                                showMoreExpanded={googleMoreOpen}
                                noOfDisplayedApps={googleMoreOpen ? relatedApps.google.apps.length : 3}
                                showMore={setGoogleMoreOpen.bind(null, true)}
                                showBack={setGoogleMoreOpen.bind(null, false)}
                                onClickHandler={isSIWebsiteAnalysisPage ? handleAppClick : undefined}
                            />
                        ) : null}
                    </RelatedAppsWrapper>
                    <RetailInsightsCta
                        href={retailIntelligenceUpsellCtaHref}
                        onClick={handleRetailInsightsClick}
                        label={translate("website_analysis.shared.website_performance_header.retail_insights_cta")}
                    />
                </Flex>
            </LeftSectionWrapper>
            <MiddleSection />
            <RightSectionWrapper>
                <MobileAndDesktopScreenshots
                    mobileImageUrl={getMobileScreenshotUrlFromDesktopImage(websiteHeaderImage)}
                    desktopImageUrl={websiteHeaderImage}
                />
            </RightSectionWrapper>
        </HeaderWrapper>
    );
};
